<template>
    <checkable-field
        :name="name"
        type="radio"
        class="tabs-toggle"
        hide-input
        required
        :class="{ narrow, underline }"
        :selected="[selected]"
        @created="created"
        @updated="updated"
    >
        <template v-for="slot of slots" :key="slot" #[slot]>
            <slot :name="slot" />
        </template>
    </checkable-field>
</template>

<script lang="ts" setup>
import { EmitEvents } from '@btcdirect/shared/src/components/types';
import { CheckableField, ValidatedFieldData } from 'out-of-the-vue';
import { useSlots } from 'vue';

const emit = defineEmits([EmitEvents.CREATED, EmitEvents.UPDATED]);

defineProps({
    name: {
        type: String,
        required: true
    },
    selected: {
        type: String,
        required: false
    },
    narrow: {
        type: Boolean,
        required: false,
        default: false
    },
    underline: {
        type: Boolean,
        required: false,
        default: false
    }
});

const slots = Object.keys(useSlots()).slice(0, 3);

const created = (data: ValidatedFieldData): void => {
    emit(EmitEvents.CREATED, data);
};

const updated = (data: ValidatedFieldData): void => {
    emit(EmitEvents.UPDATED, data);
};
</script>

<style lang="scss" scoped>
@use '@@scss/flex';
@use '@@scss/fonts';
@use '@@scss/transitions';

.input-field.tabs-toggle {
    @include flex.resize($shrink: 0);

    font-size: 1rem;
    overflow: hidden;

    &:not(.narrow) {
        height: 3.5em;
    }

    &.narrow {
        height: 2.5em;
    }

    :deep(main) {
        column-gap: 0;
        grid-template-columns: repeat(v-bind('slots.length'), 1fr);
        height: 100%;

        .checkable-field-item {
            @include flex.align($vertical: center);
            @include fonts.gt-walsheim-medium;
            min-height: auto;
            padding-left: 1em;
            padding-right: 1em;

            &:not(.selected) {
                color: var(--ootv-tabs-toggle-color);
            }

            &:hover {
                color: var(--ootv-tabs-toggle-active-color);
            }

            .content {
                text-align: center;
            }
        }
    }

    &:not(.underline) {
        border-radius: 0.5em;

        :deep(main) {
            background-color: var(--ootv-tabs-toggle-background-color);

            .checkable-field-item {
                @include transitions.transition((background-color, color));

                &.selected {
                    color: var(--ootv-tabs-toggle-active-color);
                    background-color: var(--ootv-tabs-toggle-active-background-color);
                }
            }
        }
    }

    &.underline {
        :deep(main) {
            .checkable-field-item {
                @include transitions.transition((border-color, color));

                border-bottom-width: 2px;
                border-bottom-style: solid;

                &:not(.selected) {
                    border-color: var(--ootv-tabs-toggle-active-background-color);
                }

                &.selected {
                    border-color: var(--btcdirect-blue);
                    color: var(--btcdirect-blue);
                }
            }
        }
    }
}
</style>
